import { useEffect, useState } from "react";
import { settingsAPI, usersAPI } from "../../services/api";
import { useToast } from "../../contexts/ToastContext";
import { Loading, Spinner } from "../../components/ui";

const TABS = [
  {
    key: "stitch",
    label: "✂️ Stitch Types",
    settingsKey: "stitchTypes",
    placeholder: "e.g. Pant, Shirt…",
    description: "Used while creating orders.",
  },
  {
    key: "material",
    label: "🧶 Materials",
    settingsKey: "materialTypes",
    placeholder: "e.g. Cotton, Silk…",
    description: "Material types selectable in orders.",
  },
  {
    key: "users",
    label: "👥 Users",
    settingsKey: "users",
    placeholder: "Search users...",
    description: "Manage company users.",
  },
];

const MEASUREMENT_LIMIT = 12;
const MATERIAL_UNITS = ["Nos", "Piece", "Mtr"];

export default function Settings() {
  const toast = useToast();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("stitch");
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  // ✅ MODAL
  const [showModal, setShowModal] = useState(false);
  const [showStitchModal, setShowStitchModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editMaterialItem, setEditMaterialItem] = useState(null);
  const [saving, setSaving] = useState(false);

  // ── USERS ─────────────────────────────────────────────
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const [userForm, setUserForm] = useState({
    CU_Name: "",
    CU_Email: "",
    CU_Password: "",
    CU_Role: "staff",
    CU_Status: 1,
  });

  // ✅ FORM
  const [form, setForm] = useState({
    name: "",
    price: "",
    unit: "",
  });

  const [editForm, setEditForm] = useState({
    name: "",
    price: "",
    measurements: [],
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);

    try {
      const data = await settingsAPI.get();
      setSettings(data);
    } catch (e) {
      toast("Failed to load settings", "error");
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await usersAPI.list();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      toast(e.message || "Failed to load users", "error");
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "users") loadUsers();
  }, [tab]);

  const current = TABS.find((t) => t.key === tab);

  const getMeasurements = (item) =>
    Array.from({ length: MEASUREMENT_LIMIT }, (_, i) => item[`PM_M${i + 1}`] || "")
      .filter((value) => value.trim() !== "");

  const getMeasurementPayload = (measurements) => {
    const values = measurements
      .map((value) => value.trim())
      .filter(Boolean)
      .slice(0, MEASUREMENT_LIMIT);

    return Array.from({ length: MEASUREMENT_LIMIT }).reduce((payload, _, i) => {
      payload[`PM_M${i + 1}`] = values[i] || "";
      return payload;
    }, {});
  };

  const openAddModal = () => {
    if (tab === "users") {
      setEditUser(null);
      setUserForm({
        CU_Name: "",
        CU_Email: "",
        CU_Password: "",
        CU_Role: "staff",
        CU_Status: 1,
      });
      setShowUserModal(true);
      return;
    }

    if (tab === "stitch") {
      setEditItem(null);
      setEditForm({
        name: "",
        price: "",
        measurements: [],
      });
      setShowStitchModal(true);
      return;
    }

    setEditMaterialItem(null);
    setForm({
      name: "",
      price: "",
      unit: "",
    });
    setShowModal(true);
  };

  const openMaterialEditModal = (item) => {
    setEditMaterialItem(item);
    setForm({
      name: item.MM_Name || "",
      price: "",
      unit: item.MM_Unit || "",
    });
    setShowModal(true);
  };

  const closeMaterialModal = () => {
    setShowModal(false);
    setEditMaterialItem(null);
    setForm({
      name: "",
      price: "",
      unit: "",
    });
  };

  const openEditModal = (item) => {
    setEditItem(item);
    setEditForm({
      name: item.PM_Name || "",
      price: item.PM_StichRate || "",
      measurements: getMeasurements(item),
    });
    setShowStitchModal(true);
  };

  const closeEditModal = () => {
    setShowStitchModal(false);
    setEditItem(null);
    setEditForm({
      name: "",
      price: "",
      measurements: [],
    });
  };

  const updateMeasurement = (index, value) => {
    setEditForm((prev) => ({
      ...prev,
      measurements: prev.measurements.map((item, i) =>
        i === index ? value : item
      ),
    }));
  };

  const addMeasurementField = () => {
    setEditForm((prev) => {
      if (prev.measurements.length >= MEASUREMENT_LIMIT) return prev;

      return {
        ...prev,
        measurements: [...prev.measurements, ""],
      };
    });
  };

  const removeMeasurementField = (index) => {
    setEditForm((prev) => ({
      ...prev,
      measurements: prev.measurements.filter((_, i) => i !== index),
    }));
  };

  const openUserEditModal = (user) => {
    setEditUser(user);
    setUserForm({
      CU_Name: user.CU_Name || "",
      CU_Email: user.CU_Email || "",
      CU_Password: "",
      CU_Role: user.CU_Role || "staff",
      CU_Status: Number(user.CU_Status ?? 1),
    });
    setShowUserModal(true);
  };

  const closeUserModal = () => {
    setShowUserModal(false);
    setEditUser(null);
    setUserForm({
      CU_Name: "",
      CU_Email: "",
      CU_Password: "",
      CU_Role: "staff",
      CU_Status: 1,
    });
  };

  const handleUserSave = async () => {
    if (!userForm.CU_Name.trim()) {
      toast("Name is required", "error");
      return;
    }
    if (!userForm.CU_Email.trim()) {
      toast("Email is required", "error");
      return;
    }
    if (!editUser && !userForm.CU_Password.trim()) {
      toast("Password is required", "error");
      return;
    }
    if (userForm.CU_Password.trim() && userForm.CU_Password.length < 8) {
      toast("Password must be at least 8 characters", "error");
      return;
    }

    setSaving(true);
    try {
      if (editUser) {
        await usersAPI.update({
          CU_Id: editUser.CU_Id,
          CU_Name: userForm.CU_Name.trim(),
          CU_Email: userForm.CU_Email.trim(),
          CU_Password: userForm.CU_Password,
          CU_Role: userForm.CU_Role,
          CU_Status: Number(userForm.CU_Status),
        });
        toast("User updated successfully");
      } else {
        await usersAPI.create({
          CU_Name: userForm.CU_Name.trim(),
          CU_Email: userForm.CU_Email.trim(),
          CU_Password: userForm.CU_Password,
          CU_Role: userForm.CU_Role,
        });
        toast("User created successfully");
      }
      closeUserModal();
      await loadUsers();
    } catch (e) {
      toast(e.message || "User operation failed", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    try {
      if (tab === "users") {
        if (Number(item.CU_Is_Owner) === 1) {
          toast("Company owner cannot be deleted", "error");
          return;
        }
        if (!window.confirm(`Delete user "${item.CU_Name}"?`)) return;
        await usersAPI.delete(item.CU_Id);
        toast("User deleted successfully");
        await loadUsers();
        return;
      }

      if (tab === "stitch") {
        await settingsAPI.deleteStitchType(item);
      } else {
        await settingsAPI.deleteMaterialType(item);
      }

      toast("Deleted");
      loadData();
    } catch (e) {
      toast(e?.message || "Delete failed", "error");
    }
  };

  // ✅ ADD ITEM
  const handleAdd = async () => {
    if (!form.name.trim()) {
      toast("Name is required", "error");
      return;
    }

    if (tab === "material" && !form.unit) {
      toast("Unit is required", "error");
      return;
    }

    setSaving(true);

    try {
      if (tab === "stitch") {
        await settingsAPI.addStitchType({
          PM_Name: form.name,
          PM_StichRate: form.price || 0,
        });
      } else {
        await settingsAPI.addMaterialType({
          MM_ID: editMaterialItem?.MM_ID ?? 0,
          MM_Name: form.name,
          MM_Unit: form.unit,
        });
      }

      toast(editMaterialItem ? "Material updated" : "Item added");

      closeMaterialModal();

      loadData();
    } catch {
      toast(editMaterialItem ? "Failed to update material" : "Failed to add item", "error");
    } finally {
      setSaving(false);
    }
  };

  // ✅ SAFE ARRAY HANDLING
  const handleStitchSave = async () => {
    if (!editForm.name.trim()) {
      toast("Name is required", "error");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        PM_Name: editForm.name.trim(),
        PM_StichRate: editForm.price || 0,
        ...getMeasurementPayload(editForm.measurements),
      };

      if (editItem) {
        await settingsAPI.updateStitchType({
          ...payload,
          PM_ID: editItem.PM_ID,
          PM_IsHide: editItem.PM_IsHide ?? 0,
        });
      } else {
        await settingsAPI.addStitchType(payload);
      }

      toast(editItem ? "Stitch type updated" : "Stitch type added");
      closeEditModal();
      loadData();
    } catch {
      toast(editItem ? "Failed to update stitch type" : "Failed to add stitch type", "error");
    } finally {
      setSaving(false);
    }
  };

  const currentList =
    tab === "users"
      ? users
      : Array.isArray(settings?.[current.settingsKey])
        ? settings[current.settingsKey]
        : [];

  // ✅ FILTERED LIST
  const filteredList = currentList.filter((item) => {
    const name =
      tab === "users"
        ? `${item.CU_Name || ""} ${item.CU_Email || ""}`.toLowerCase()
        : (item.PM_Name || item.MM_Name || "").toLowerCase();

    return name.includes(search.toLowerCase());
  });

  return (
    <div className="page">

      {/* PAGE HEADER */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Settings</h1>

          <p className="page-sub">
            Configure stitch types, materials, and company users used across the app
          </p>
        </div>

        {/* ✅ ADD BUTTON */}
        <button
          className="btn btn-primary"
          onClick={openAddModal}
        >
          ＋ {tab === "users" ? "Add User" : "Add New"}
        </button>
      </div>

      <div className="card">

        {/* TABS */}
        <div className="section-tabs">
          {TABS.map((t) => (
            <div
              key={t.key}
              className={`section-tab${tab === t.key ? " active" : ""}`}
              onClick={() => {
                setTab(t.key);
                setSearch("");
                setExpandedId(null);
              }}
            >
              {t.label}
            </div>
          ))}
        </div>

        {loading || (tab === "users" && usersLoading) ? (
          <Loading />
        ) : (
          <>
            {/* SEARCH */}
            <div
              className="search-bar"
              style={{ marginBottom: 15 }}
            >
              <span className="search-icon">🔍</span>

              <input
                className="form-input"
                placeholder={
                  tab === "users"
                    ? "Search by name or email..."
                    : `Search ${tab} items...`
                }
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* TABLE */}
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    {tab === "users" ? (
                      <>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </>
                    ) : (
                      <>
                        <th>Item Name</th>
                        <th>Status</th>
                        <th>
                          {tab === "stitch" ? "Price" : "Unit"}
                        </th>
                        <th>Actions</th>
                      </>
                    )}
                  </tr>
                </thead>

                <tbody>

                  {/* EMPTY STATE */}
                  {filteredList.length === 0 ? (
                    <tr>
                      <td colSpan={tab === "users" ? 5 : 4}>
                        <div className="empty-state">

                          <div className="empty-icon">
                            📦
                          </div>

                          <div className="empty-title">
                            No{" "}
                            {tab === "users"
                              ? "users"
                              : tab === "material"
                                ? "materials"
                                : "stitch types"}{" "}
                            found
                          </div>

                          <div className="empty-sub">
                            Click on {tab === "users" ? "Add User" : "Add New"} to create one.
                          </div>

                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredList.map((item) => {
                      if (tab === "users") {
                        return (
                          <tr key={item.CU_Id}>
                            <td>
                              <div>{item.CU_Name}</div>
                              {Number(item.CU_Is_Owner) === 1 && (
                                <div className="td-muted">Company Owner</div>
                              )}
                            </td>
                            <td className="td-code">{item.CU_Email}</td>
                            <td>
                              <span className="badge">
                                {item.CU_Role || "staff"}
                              </span>
                            </td>
                            <td>
                              <span
                                className={
                                  Number(item.CU_Status) === 1
                                    ? "badge badge-green"
                                    : "badge badge-red"
                                }
                              >
                                {Number(item.CU_Status) === 1 ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td
                              className="td-actions"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                className="btn btn-xs btn-secondary"
                                onClick={() => openUserEditModal(item)}
                              >
                                ✏️
                              </button>
                              {Number(item.CU_Is_Owner) !== 1 && (
                                <button
                                  className="btn btn-xs btn-danger"
                                  onClick={() => handleDelete(item)}
                                >
                                  🗑
                                </button>
                              )}
                            </td>
                          </tr>
                        );
                      }

                      const id =
                        item.PM_ID || item.MM_ID;

                      const name =
                        item.PM_Name || item.MM_Name;

                      return (
                        <>
                          {/* MAIN ROW */}
                          <tr
                            key={id}
                            onClick={() =>
                              setExpandedId(
                                expandedId === id
                                  ? null
                                  : id
                              )
                            }
                          >
                            <td>
                              <div>{name}</div>

                              <div className="td-muted">
                                {tab === "stitch"
                                  ? "Basic stitching"
                                  : "Material item"}
                              </div>
                            </td>

                            <td>
                              <span className="badge badge-green">
                                Active
                              </span>
                            </td>

                            <td className="td-code">
                              {tab === "stitch"
                                ? `₹${item.PM_StichRate || 0}`
                                : item.MM_Unit || "-"}
                            </td>

                            <td
                              className="td-actions"
                              onClick={(e) =>
                                e.stopPropagation()
                              }
                            >
                              <button
                                className="btn btn-xs btn-secondary"
                                onClick={() => {
                                  if (tab === "stitch") {
                                    openEditModal(item);
                                  } else {
                                    openMaterialEditModal(item);
                                  }
                                }}
                              >
                                ✏️
                              </button>

                              <button
                                className="btn btn-xs btn-danger"
                                onClick={() =>
                                  handleDelete(item)
                                }
                              >
                                🗑
                              </button>
                            </td>
                          </tr>

                          {/* EXPANDED ROW */}
                          {expandedId === id &&
                            tab === "stitch" && (
                              <tr>
                                <td colSpan="4">
                                  <div
                                    className="card"
                                    style={{
                                      marginTop: 10,
                                    }}
                                  >

                                    <div className="card-section-title">
                                      <span>📏</span>
                                      Measurements
                                    </div>

                                    {/* DYNAMIC MEASUREMENTS */}
                                    <div className="meas-grid">
                                      {Array.from(
                                        { length: MEASUREMENT_LIMIT },
                                        (_, i) => {
                                          const key = `PM_M${
                                            i + 1
                                          }`;

                                          const label =
                                            item[key];

                                          if (
                                            !label ||
                                            label.trim() ===
                                              ""
                                          )
                                            return null;

                                          return (
                                            <div
                                              key={key}
                                              className="meas-item"
                                            >
                                              <div className="meas-label">
                                                {
                                                  label
                                                }
                                              </div>
                                            </div>
                                          );
                                        }
                                      )}
                                    </div>

                                  </div>
                                </td>
                              </tr>
                            )}
                        </>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* USER MODAL */}
      {showUserModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  {editUser ? "Edit User" : "Add New User"}
                </div>
                <div className="modal-subtitle">
                  {editUser
                    ? "Update the user account details."
                    : "Create a new user for your company."}
                </div>
              </div>
              <button className="btn-close" onClick={closeUserModal}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">Name</label>
                  <input
                    className="form-input"
                    placeholder="Enter user name"
                    value={userForm.CU_Name}
                    onChange={(e) =>
                      setUserForm({ ...userForm, CU_Name: e.target.value })
                    }
                  />
                </div>

                <div className="form-group form-full">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    placeholder="Enter email address"
                    value={userForm.CU_Email}
                    onChange={(e) =>
                      setUserForm({ ...userForm, CU_Email: e.target.value })
                    }
                  />
                </div>

                <div className="form-group form-full">
                  <label className="form-label">
                    Password
                    {editUser && (
                      <span className="td-muted">
                        {" "} (leave blank to keep current password)
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    className="form-input"
                    placeholder={editUser ? "Enter new password" : "Enter password"}
                    value={userForm.CU_Password}
                    onChange={(e) =>
                      setUserForm({ ...userForm, CU_Password: e.target.value })
                    }
                  />
                </div>

                <div className="form-group form-full">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={userForm.CU_Role}
                    onChange={(e) =>
                      setUserForm({ ...userForm, CU_Role: e.target.value })
                    }
                  >
                    <option value="staff">Staff</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>

                {editUser && (
                  <div className="form-group form-full">
                    <label className="form-label">Status</label>
                    <select
                      className="form-select"
                      value={userForm.CU_Status}
                      onChange={(e) =>
                        setUserForm({
                          ...userForm,
                          CU_Status: Number(e.target.value),
                        })
                      }
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeUserModal}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUserSave}
                disabled={saving}
              >
                {saving ? <Spinner /> : editUser ? "Save Changes" : "Create User"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ ADD MODAL */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">

            {/* HEADER */}
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  {editMaterialItem ? "Edit Material" : "Add New Material"}
                </div>

                <div className="modal-subtitle">
                  {editMaterialItem
                    ? "Update the material name and unit."
                    : "Create a new material item."}
                </div>
              </div>

              <button
                className="btn-close"
                onClick={closeMaterialModal}
              >
                ✕
              </button>
            </div>

            {/* BODY */}
            <div className="modal-body">

              <div className="form-grid">

                {/* NAME */}
                <div className="form-group form-full">
                  <label className="form-label">
                    Name
                  </label>

                  <input
                    className="form-input"
                    placeholder={
                      tab === "stitch"
                        ? "e.g. Shirt"
                        : "e.g. Cotton"
                    }
                    value={form.name}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                {/* STITCH PRICE */}
                {tab === "stitch" && (
                  <div className="form-group form-full">
                    <label className="form-label">
                      Stitch Price
                    </label>

                    <input
                      type="number"
                      className="form-input"
                      placeholder="Enter stitch price"
                      value={form.price}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          price: e.target.value,
                        })
                      }
                    />
                  </div>
                )}

                {/* MATERIAL UNIT */}
                {tab === "material" && (
                  <div className="form-group form-full">
                    <label className="form-label">
                      Unit
                    </label>

                    <select
                      className="form-select"
                      value={form.unit}
                      onChange={(e) =>
                        setForm({
                          ...form,
                          unit: e.target.value,
                        })
                      }
                    >
                      <option value="">Select unit</option>
                      {MATERIAL_UNITS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

              </div>
            </div>

            {/* FOOTER */}
            <div className="modal-footer">

              <button
                className="btn btn-secondary"
                onClick={closeMaterialModal}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={handleAdd}
                disabled={saving}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  editMaterialItem ? "Save Changes" : "Save"
                )}
              </button>

            </div>

          </div>
        </div>
      )}

      {showStitchModal && (
        <div className="modal-overlay">
          <div className="modal modal-lg">
            <div className="modal-header">
              <div>
                <div className="modal-title">
                  {editItem ? "Edit Stitch Type" : "Add Stitch Type"}
                </div>

                <div className="modal-subtitle">
                  {editItem
                    ? "Update the name and measurement fields for this stitch type."
                    : "Create a stitch type with its measurement fields."}
                </div>
              </div>

              <button
                className="btn-close"
                onClick={closeEditModal}
              >
                x
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group form-full">
                  <label className="form-label">
                    Stitch Name
                  </label>

                  <input
                    className="form-input"
                    placeholder="e.g. Shirt"
                    value={editForm.name}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group form-full">
                  <label className="form-label">
                    Stitch Price
                  </label>

                  <input
                    type="number"
                    className="form-input"
                    placeholder="Enter stitch price"
                    value={editForm.price}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        price: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group form-full">
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                      marginBottom: 8,
                    }}
                  >
                    <label className="form-label" style={{ margin: 0 }}>
                      Measurement Types ({editForm.measurements.length}/{MEASUREMENT_LIMIT})
                    </label>

                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={addMeasurementField}
                      disabled={editForm.measurements.length >= MEASUREMENT_LIMIT}
                    >
                      Add Measurement
                    </button>
                  </div>

                  {editForm.measurements.length === 0 ? (
                    <div className="td-muted">
                      No measurements added yet. Use Add Measurement to create one.
                    </div>
                  ) : (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        gap: 10,
                      }}
                    >
                      {editForm.measurements.map((measurement, index) => (
                        <div
                          key={index}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto",
                            gap: 8,
                            alignItems: "center",
                          }}
                        >
                          <input
                            className="form-input"
                            placeholder={`Measurement ${index + 1}`}
                            value={measurement}
                            onChange={(e) =>
                              updateMeasurement(index, e.target.value)
                            }
                          />

                          <button
                            className="btn btn-xs btn-danger"
                            onClick={() => removeMeasurementField(index)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={closeEditModal}
              >
                Cancel
              </button>

              <button
                className="btn btn-primary"
                onClick={handleStitchSave}
                disabled={saving}
              >
                {saving ? (
                  <Spinner />
                ) : (
                  editItem ? "Save Changes" : "Save"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
